import { useTranslation } from 'react-i18next';
import { Reveal } from '@/components/ui/reveal';
import TopoLines from '@/components/marketing/TopoLines';
import ControlOverlapFigure from '@/components/marketing/ControlOverlapFigure';
import ControlOutputs from '@/components/marketing/ControlOutputs';
import AnchorNav from '@/components/marketing/AnchorNav';

/**
 * The position section (Messaging Architecture v1.0, homepage §3).
 *
 * This is SecondFrameworkProblem and WhatWeActuallyDo merged. They were the
 * page's only two Brand Ink bands, six sections apart, and they said the same
 * thing at their seam: the first closed on "One control set. Every framework
 * you carry.", the second opened on "One control. Every artifact the audit asks
 * for." ControlOutputs' own header comment already described the overlap
 * figure as "the section above" — it had not been the section above for a
 * while. This restores that.
 *
 * One spine, and the two figures are the two sides of the same control:
 *
 *   problem      Framework one is a product. Framework two is a project.
 *   figure A     ControlOverlapFigure — the frameworks that control satisfies
 *   pivot        cSquare is built for the middle.
 *   figure B     ControlOutputs — the artifacts that same control produces
 *   close        the ask
 *
 * whatWeActuallyDo.title now captions figure B instead of heading its own
 * section, so no locale file changed. whatWeActuallyDo.eyebrow and .subtitle
 * are no longer rendered anywhere.
 *
 * Impact on this page comes from rhythm — alternating light and dark — not
 * from saturation. Merging spent one of the two dark anchors, so FinalCTABand
 * carries the second one now.
 *
 * NO CTA HERE, deliberately. This section used to close on "Ready to transform
 * your compliance?" with Get Started and Schedule a Demo. FinalCTABand is now
 * also Brand Ink and makes the same offer in nearly the same words, which made
 * it the third identical ask on one page after the hero's. The section ends on
 * the argument and lets the close do the closing. whatWeActuallyDo's
 * readyToTransform, getStartedFree and scheduleDemo keys are no longer
 * rendered.
 */
const OneControl = () => {
  const { t } = useTranslation();

  return (
    <section className="relative overflow-hidden bg-brand-ink py-14 text-brand-ink-foreground lg:py-20 laptop:py-10">
      {/* Texture runs the full band. ControlOutputs lays its own scrim behind
          the fan so the contour lines never fight the connector curves. */}
      <TopoLines opacity={0.28} />

      <div className="container relative mx-auto max-w-6xl px-6">
        <div className="max-w-4xl">
          <Reveal>
            <h2 className="max-w-3xl text-balance font-bold section-title-fluid">
              {t('secondFramework.title')}
            </h2>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-6 laptop:mt-4 max-w-2xl text-lg leading-relaxed text-brand-ink-foreground/80">
              {t('secondFramework.paraOne')}
            </p>
          </Reveal>
        </div>

        {/* Figure A — what the control answers to. */}
        <Reveal delay={0.15}>
          <div className="mx-auto mt-10 laptop:mt-6 max-w-4xl border border-brand-ink-foreground/15 bg-brand-ink-raised p-5 sm:p-6 laptop:p-4">
            <ControlOverlapFigure />
          </div>
        </Reveal>

      </div>

      {/* The pivot, as a full-bleed light band — the same device as
          TodayOnTheTrail, borrowed here for contrast: a divider that happens to
          carry the one line the section exists to land.
          
          It runs edge to edge rather than sitting inside the container, so the
          dark band reads as two halves either side of a rule: what the control
          answers to above, what it produces below. That is the visual split
          without an actual second <section> — the two figures still share one
          heading, one set of padding and one control node, which is the whole
          reason they were merged.
          
          Brand Ink lifted, in the spirit of Footer's top CTA strip, with
          hairlines top and bottom. Between two dark halves a light band
          shouted; this marks the seam without breaking the band.
          
          SOLID, not a translucent overlay: the section's contour texture runs
          the full band, and at 5% opacity the wave read straight through the
          shelf. brand-ink-raised is opaque, so the seam is clean. */}
      <Reveal delay={0.2}>
        <blockquote className="relative z-10 mt-12 border-t border-brand-ink-foreground/10 bg-brand-ink-raised text-brand-ink-foreground lg:mt-14 laptop:mt-6">
          <div className="container mx-auto max-w-6xl px-6 py-3.5">
            <div className="flex flex-col items-center gap-1 text-center sm:flex-row sm:items-baseline sm:justify-center sm:gap-3">
              <p className="flex shrink-0 items-baseline gap-1.5 text-base font-semibold sm:text-lg">
                <span aria-hidden className="text-xl leading-none text-trail-teal sm:text-2xl">
                  &ldquo;
                </span>
                {t('secondFramework.closingLead')}
              </p>
              <p className="text-pretty text-sm leading-snug text-brand-ink-foreground/70 sm:text-base">
                {t('secondFramework.closingBody')}
              </p>
            </div>
          </div>
        </blockquote>
      </Reveal>

      {/* The shelf's second row: where to go once the pivot has landed. */}
      <div className="relative z-10 mb-12 lg:mb-14 laptop:mb-8">
        <AnchorNav />
      </div>

      <div className="container relative mx-auto max-w-6xl px-6">
        {/* Figure B — what that same control produces. */}
        <Reveal delay={0.15}>
          <div>
            <p className="eyebrow text-trail-teal">{t('whatWeActuallyDo.eyebrow')}</p>
            <h3 className="mt-4 max-w-3xl text-balance text-2xl font-semibold lg:text-3xl">
              {t('whatWeActuallyDo.title')}
            </h3>
            <p className="mt-4 max-w-3xl text-lg leading-relaxed text-brand-ink-foreground/75">
              {t('whatWeActuallyDo.subtitle')}
            </p>
            <div className="mt-10 lg:mt-12">
              <ControlOutputs />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default OneControl;
